/**
 * DocumentsDashboard_CypAdhd — documents page for a CYP ADHD patient.
 *
 * VISUAL DESIGN IS THE PLATFORM'S, NOT ITS OWN: this is the same "Report
 * Builder" pipeline the adult ADHD and autism documents pages use — a patient
 * header panel, then one white card holding every stage as a vertical pipeline
 * of numbered circles joined by a connecting line, each row carrying a status
 * icon, an assignment label, the assigned clinician's pill and its actions.
 * The row/circle/icon styling is transcribed from
 * DocumentsDashboard_Autism.js (the modular sibling, same data shape) so a
 * clinician moving between a child and an adult sees one page, not two.
 *
 * RENDERING IS LIVE for all four stages (CI, SSI, DD, PAD): approving a
 * stage's review triggers renderAndStoreCypAdhdDocument via the dedicated CYP
 * templates (functions/cyp_car/render.py). Each rendered row offers Preview
 * (adult style, magnifying glass); DOWNLOADS ARE PANEL-LEVEL (Sai, 28 Aug) —
 * the Download Sections button opens the adult ReportBundleDialog, the
 * clinician picks sections, and generateClinicalReportBundle stitches one PDF.
 *
 * The purple ready-for-review state is gated on the MODULE BEING FINISHED,
 * not on paragraphs existing (Sai, 28 Aug — adult semantics). While the
 * renderer runs a row carries the adult amber "Finalising..." state.
 *
 * The Patient Report (its own Additional Documents panel below) AUTO-FINALISES
 * once all four section reports are approved (CI + SSI + DD + PAD), mirroring
 * the Adult Autism pattern. No manual Generate button — the review exit and
 * the mount kick fire rerenderCypAdhdPatientReportIfStale. A 15-min
 * clinician-only self-heal resets stuck renders to 'error'.
 */

import React, { useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { doc as firestoreDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { toast } from 'react-toastify';
import {
  LockClosedIcon, CheckCircleIcon, DocumentTextIcon, SearchIcon, DownloadIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/outline';
import { ChevronRightIcon } from '@heroicons/react/solid';
import { Tooltip } from 'react-tooltip';
import ReportBundleDialog from './components/ReportBundleDialog';
import {
  getManageDocumentsPanelCopy,
  getAdditionalDocumentsPanelTitle,
} from './documentsDashboardHelpers';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PatientContractTypeBadge from '../../../components/PatientContractTypeBadge';
import CypGuardianContactLines from '../../../components/CypGuardianContactLines';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { TriggerRegenerateCypAdhdStage } from '../../../utils/TriggerRegenerateCypAdhdStage';
import { hasCypCiPageSectionData } from '../../../utils/cypCiPageSectionData';
import { auth, db } from '../../../firebase';
import { getCypAdhdReportContentPathSegments } from '../../../constants/cypAdhdFirestorePaths';
import { TriggerRenderCypAdhdDocTemplate } from '../../../utils/TriggerRenderCypAdhdDocTemplate';
import { rerenderCypAdhdPatientReportIfStale } from '../../../utils/autoCypAdhdPatientReport';
import { getStaleCypPatientReportReset } from '../../../utils/processingStatusHelpers';
import { STAGE_TO_CLINICIAN_KEY, STAGE_KEY_TO_CYP_REPORT_STAGE, CYP_PATIENT_REPORT_STAGE } from '../../../constants/cypAdhdStages';
import { useAuth } from '../../../hooks/useAuth';
import { formatDateTime } from '../../../utils/timestampUtils';
import { findInformantAnswersChangedAfterGeneration } from '../../../utils/formLockingUtils';
import { DocumentEmbedDialog } from '../8_diagnosticDecision/components/DocumentEmbedDialog';
import { TriggerFetchLatestDocForDownload } from '../../../utils/TriggerFetchLatestDocForDownload';
import { isPdfReady, getPdfAvailability } from '../../../utils/pdfAvailability';
import DownloadDropdown from './components/DownloadDropdown';

// Slots whose AI outputs have a live review page (ASMT-21) carry a
// reviewRoute and the reportContent stage key + approval flag the review
// exit writes.
//
// regenerateSections lists the report sections a regenerate must cover; they
// must match the backend SECTION_PARAGRAPHS keys for that stage exactly, or a
// section would be left holding stale paragraphs.
// The stages the CYP renderer can produce, with the status-field prefix each
// one's reportContent doc carries (STAGE_TO_REPORT_PREFIX values).
// patientReport joins the render-status listeners but is excluded from the
// bundle dialog (it has its own PDF/DOCX download).
const RENDERABLE_STAGES = [
  { stageKey: 'clinicalInterview', prefix: 'ci' },
  { stageKey: 'semiStructuredInterview', prefix: 'ssi' },
  { stageKey: 'diagnosticDecision', prefix: 'dd' },
  { stageKey: 'postAssessmentDiscussion', prefix: 'pad' },
  { stageKey: 'ratingScales', prefix: 'ratingScales' },
  { stageKey: 'patientReport', prefix: 'patientReport' },
];

// Regenerate stays wired but HIDDEN (Sai, 28 Aug: "No need to show the
// Re-generate AI outputs button (but keep the functionality in code)").
// Flip to true to surface it again.
export const SHOW_REGENERATE_AI_OUTPUTS_BUTTON = false;

// Bundle-dialog section ids for the CYP stages. These must match the
// frontend ReportBundleDialog mapping AND the backend allowlist in
// document_functions.generateClinicalReportBundle — all three name the same
// documents.{key} slots.
const STAGE_TO_BUNDLE_SECTION_ID = {
  clinicalInterview: 'cypAdhdClinicalInterview',
  semiStructuredInterview: 'cypAdhdSemiStructuredInterview',
  diagnosticDecision: 'cypAdhdDiagnosticDecision',
  postAssessmentDiscussion: 'cypAdhdPostAssessmentDiscussion',
  ratingScales: 'cypAdhdRatingScales',
};

// Panel copy from the shared helper, resolved for a Report Builder org (which
// Well Life Clinic is): panelTitle "Report Builder", download button
// "Download Sections", and the Additional Documents panel titled "Final
// Assessment Report". Taken from the helper rather than hardcoded so this page
// cannot drift from the adult dashboard's wording.
const PANEL_COPY = getManageDocumentsPanelCopy({
  replaceCARwithReportBuilder: true,
  isTOCWorkflow: false,
});

export const DOCUMENT_SLOTS = [
  {
    order: 1,
    key: 'cypAdhdCiReport', title: 'Clinical Interview',
    stageKey: 'clinicalInterview', approvedFlag: 'ciReportApproved',
    reviewRoute: (patientId) => `/patient/${patientId}/review-document-ci-cyp-adhd`,
    regenerateSections: [
      'Presenting Complaint',
      'Education History',
      'Developmental History',
      'Social History',
      'Family History',
      'Medical History',
      'Mental Health History',
      'Mental State Examination',
      'Risk Assessment',
    ],
  },
  {
    order: 2,
    key: 'cypAdhdSsiReport', title: 'Semi-Structured Interview',
    stageKey: 'semiStructuredInterview', approvedFlag: 'ssiReportApproved',
    reviewRoute: (patientId) => `/patient/${patientId}/review-document-ssi-cyp-adhd`,
    regenerateSections: ['Symptoms', 'Functional Impairment'],
  },
  {
    order: 3,
    key: 'cypAdhdDdReport', title: 'Diagnostic Decision',
    stageKey: 'diagnosticDecision', approvedFlag: 'ddReportApproved',
    reviewRoute: (patientId) => `/patient/${patientId}/review-document-dd-cyp-adhd`,
    regenerateSections: ['Diagnostic Decision', 'Secondary Diagnoses', 'Formulation'],
  },
  // The Post-Assessment Discussion is a document the family receives in its
  // own right (Eve, 1 Sept), not only a section of the Patient Report.
  //
  // Review of Assessment, Treatment Options, and Health Checks each draft
  // paragraphs. Regenerating must name every section, or a later section
  // would keep a stale draft. Letter ticks are not part of any draft.
  {
    order: 4,
    key: 'cypAdhdPadReport', title: 'Post-Assessment Discussion',
    stageKey: 'postAssessmentDiscussion', approvedFlag: 'padReportApproved',
    reviewRoute: (patientId) => `/patient/${patientId}/review-document-pad-cyp-adhd`,
    regenerateSections: ['Review of Assessment', 'Treatment Options', 'Health Checks'],
  },
  // The Rating Scales appendix (adult parity: "Attachments - Rating Scales").
  // One scale for CYP — the SNAP-IV, answered by both informants.
  //
  // Not an assessment stage and not AI-drafted: it prints one sentence per
  // informant, verbatim from the scores the live scorer wrote on submission
  // (python-functions/functions/cyp_car/rating_scales_data.py). So it has no
  // module to finish and no paragraphs to review — it is generatable on
  // demand as soon as one informant has submitted a scale.
  {
    order: 5,
    key: 'cypAdhdRatingScalesReport',
    title: 'Attachments - Rating Scales',
    stageKey: 'ratingScales',
    reportStage: 'CYP ADHD Rating Scales',
    isRatingScales: true,
    hasNoAiOutputs: true,
    generateOnDemand: true,
  },
  // The Patient Report lives in its own Additional Documents panel below,
  // exactly as on the adult dashboard (Sai, 28 Aug).
];

/**
 * Row background. Transcribed from the autism dashboard: amber while work is
 * in flight (regenerating OR the renderer finalising), otherwise tinted to
 * the status so the pipeline scans at a glance.
 */
function backgroundClass(status, isBusy) {
  if (isBusy) return 'bg-amber-50 hover:bg-amber-100';
  switch (status) {
    case 'approved':
      return 'bg-green-50 hover:bg-green-100';
    case 'ready-for-review':
      return 'bg-violet-50 hover:bg-violet-100';
    default:
      return 'bg-gray-50 hover:bg-gray-100';
  }
}

/** Numbered-circle classes, matching the row tint. */
function circleClass(status, isBusy) {
  if (isBusy) return 'bg-amber-50 border-amber-200 text-amber-700';
  switch (status) {
    case 'approved':
      return 'bg-green-50 border-green-200 text-green-700';
    case 'ready-for-review':
      return 'bg-violet-50 border-violet-200 text-violet-700';
    default:
      return 'bg-gray-50 border-gray-200 text-gray-700';
  }
}

/**
 * Inline status indicator. Never colour alone — each state pairs its icon with
 * the assignment text beside it, per the semantic-colour rule.
 *
 * isFinalising is the adult dashboard's amber "Finalising..." state: the
 * document renderer is running for this row (Sai, 28 Aug — see the adult
 * getStatusIcon for the reference).
 */
function StatusIcon({ status, isRegenerating, isFinalising, isPending }) {
  if (isRegenerating || isFinalising) {
    return (
      <div className="flex items-center gap-2 text-amber-600">
        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span className="text-sm font-medium">{isRegenerating ? 'Regenerating...' : 'Finalising...'}</span>
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="flex items-center gap-2 text-amber-600">
        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span className="text-sm font-medium">Preparing PDF</span>
      </div>
    );
  }
  switch (status) {
    case 'approved':
      return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
    case 'ready-for-review':
      return <DocumentTextIcon className="w-5 h-5 text-violet-600" />;
    case 'locked':
      return <LockClosedIcon className="w-5 h-5 text-gray-400" />;
    default:
      return null;
  }
}

/** Who the row belongs to, phrased for its state (autism wording). */
/**
 * The reportStage string a rendered document is stored under — what Preview
 * and Download pass to the backend, and what titles the preview dialog.
 *
 * NOT read from STAGE_KEY_TO_CYP_REPORT_STAGE: that map routes LLM DRAFT
 * generation, and the appendix has no drafting pipeline, so adding it there
 * would advertise it as regeneratable. Reading it from that map is what made
 * the appendix's preview open as "undefined Report". Slots carry their own
 * label instead; it must match the registry's report_stage in
 * python-functions/functions/cyp_car/render.py.
 */
function slotReportStage(slot) {
  return slot.reportStage || STAGE_KEY_TO_CYP_REPORT_STAGE[slot.stageKey];
}

function assignmentLabel(status, isPlaceholder) {
  if (isPlaceholder) return 'Assigned to';
  switch (status) {
    case 'approved':
      return 'AI outputs last approved by';
    case 'ready-for-review':
      return 'AI outputs ready for review by';
    default:
      return 'Assigned to';
  }
}

// Rating Scales is a shared appendix, not a stage one clinician owns. The
// adult ADHD row therefore names every primary clinician assigned to the
// child (the interview, the semi-structured interview, the decision, the
// discussion, and the lead), one pill each. The same person on two stages
// appears once. Supporting clinicians on the decision are left off: they
// are shown on the decision row, and they do not generate this appendix.
const RATING_SCALES_ASSIGNMENT_STAGES = [
  { key: 'clinicianCI', label: 'Clinical Interview' },
  { key: 'clinicianSSI', label: 'Semi-Structured Interview' },
  { key: 'clinicianDD', label: 'Diagnostic Decision' },
  { key: 'clinicianPAD', label: 'Post-Assessment Discussion' },
  { key: 'clinicianLead', label: 'Lead Clinician' },
];

function ratingScalesAssignees(clinicians) {
  const byId = new Map();
  RATING_SCALES_ASSIGNMENT_STAGES.forEach(({ key, label }) => {
    const entry = clinicians?.[key];
    const id = entry?.clinicianId;
    if (!id) return;
    if (!byId.has(id)) {
      byId.set(id, { id, name: entry.clinicianName || 'Unknown', stages: [] });
    }
    byId.get(id).stages.push(label);
  });
  return Array.from(byId.values()).map((rec) => ({
    id: rec.id,
    name: rec.name,
    tooltip: `Assigned stages: ${rec.stages.join(', ')}`,
  }));
}

export default function DocumentsDashboardCypAdhd() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { claims } = useAuth();
  const {
    header, reportContent, assessmentStages, refreshReportContent, fetchPatientData,
    informantFormInstances,
  } = useContext(ModularPatientDataContext);

  // Live per-stage render status, the autism dashboard's pattern: one
  // onSnapshot per renderable stage's reportContent doc. Without it the row
  // read a one-shot fetch, so the ~40s between approving a review and the
  // renderer finishing showed "Awaiting template" — wrong twice over (the
  // template exists, and something IS happening). When a stage flips to
  // 'complete', the patient header is re-fetched so documents.{key} appears
  // and the row grows its Download/Preview without a reload.
  // Refresh on arrival, exactly as the adult dashboard does
  // (DocumentsDashboard.js: fetchPatientData(patientId, true) on mount).
  // Without it this page renders whatever reportContent the context happened
  // to cache, so an approval made earlier in the session showed as locked
  // until an unrelated render transition forced a refetch — a clinician who
  // had just approved a section saw "Available after the assessment stage is
  // completed" (Eve, 1 Sept).
  useEffect(() => {
    if (!patientId) return;
    fetchPatientData(patientId);
  }, [patientId, fetchPatientData]);

  const [renderStatuses, setRenderStatuses] = useState({});
  const prevStatusesRef = useRef({});
  useEffect(() => {
    if (!patientId) return undefined;
    const unsubs = RENDERABLE_STAGES.map(({ stageKey, prefix }) => onSnapshot(
      firestoreDoc(db, ...getCypAdhdReportContentPathSegments(patientId, stageKey)),
      (snap) => {
        const status = snap.data()?.[`${prefix}ReportProcessingStatus`] || 'idle';
        setRenderStatuses((previous) => ({ ...previous, [stageKey]: status }));
        if (status === 'complete' && prevStatusesRef.current[stageKey] === 'processing') {
          fetchPatientData(patientId);
        }
        prevStatusesRef.current[stageKey] = status;
      },
      (err) => console.error(`CYP ${stageKey} reportContent listener error:`, err),
    ));
    return () => unsubs.forEach((unsub) => unsub());
  }, [patientId, fetchPatientData]);

  // ── Mount kick: auto-finalise the Patient Report if conditions are met ──
  // When a clinician navigates to the dashboard and all four sections are
  // approved but the Patient Report is stale or missing, kick off a render.
  // Clinician-only: Support/OrgAdmin cannot write to CYP reportContent docs.
  // Runs once per mount (patientId), not on every reportContent change.
  const mountKickFiredRef = useRef(false);
  useEffect(() => {
    if (!patientId || mountKickFiredRef.current) return;
    const isClinician = claims?.userType === 'Provider';
    if (!isClinician) return;
    mountKickFiredRef.current = true;
    try {
      rerenderCypAdhdPatientReportIfStale({
        patientId,
        clinicianId: auth.currentUser?.uid,
      })?.catch?.((err) => console.error('CYP dashboard mount kick failed:', err));
    } catch (err) {
      console.error('CYP dashboard mount kick failed:', err);
    }
  }, [patientId, claims?.userType]);

  // ── Self-heal: reset stuck Patient Report renders (15 min) ──────────────
  // Mirrors the Autism dashboard's getStaleAutismReportReset. The CYP Patient
  // Report processing status lives on the reportContent/patientReport doc.
  // Clinician-only: Support cannot write to CYP reportContent docs.
  useEffect(() => {
    if (!patientId) return;
    const isClinician = claims?.userType === 'Provider';
    if (!isClinician) return;
    const prDoc = reportContent?.patientReport;
    const resets = getStaleCypPatientReportReset(prDoc) || {};
    if (!resets || Object.keys(resets).length === 0) return;
    const prRef = firestoreDoc(
      db, ...getCypAdhdReportContentPathSegments(patientId, 'patientReport'),
    );
    updateDoc(prRef, resets).catch((err) =>
      console.error('CYP patient report self-heal failed:', err),
    );
  }, [patientId, reportContent?.patientReport, claims?.userType]);

  // Answers-changed-after-generation: for each informant, detect form
  // instances updated after the CI or SSI report was generated. The result
  // is per-informant with `ci` and/or `ssi` sub-entries. We surface a row-
  // level warning on the CI and SSI rows so the clinician knows the drafted
  // paragraphs may be stale.
  const answersChanged = useMemo(
    () => findInformantAnswersChangedAfterGeneration({
      informantFormInstances: informantFormInstances || {},
      reportContent: reportContent || {},
    }),
    [informantFormInstances, reportContent],
  );

  // True when any informant updated answers after the CI report was generated.
  const ciAnswersChanged = useMemo(
    () => Object.values(answersChanged).some((e) => e.ci),
    [answersChanged],
  );
  // True when any informant updated answers after the SSI report was generated.
  const ssiAnswersChanged = useMemo(
    () => Object.values(answersChanged).some((e) => e.ssi),
    [answersChanged],
  );

  const [retryingStage, setRetryingStage] = useState(null);
  const handleRetryRender = useCallback(async (stageKey) => {
    setRetryingStage(stageKey);
    try {
      await TriggerRenderCypAdhdDocTemplate({
        patientId,
        stageKey,
        clinicianId: auth.currentUser?.uid,
      });
    } finally {
      setRetryingStage(null);
    }
  }, [patientId]);

  // Rows with no module and no review (the Rating Scales appendix) are
  // generated straight from the button — the scores exist as soon as an
  // informant submits, so there is nothing to wait for.
  const [generatingStage, setGeneratingStage] = useState(null);
  const handleGenerateOnDemand = useCallback(async (stageKey) => {
    setGeneratingStage(stageKey);
    try {
      // The trigger reports failure as { success: false, error } rather than
      // throwing — inspect the result or every failure is silent.
      const result = await TriggerRenderCypAdhdDocTemplate({
        patientId,
        stageKey,
        clinicianId: auth.currentUser?.uid,
      });
      if (!result?.success) {
        toast.error(`Document generation failed: ${result?.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('CYP on-demand document generation failed:', error);
      toast.error(`Document generation failed: ${error.message || 'Unknown error'}`);
    } finally {
      setGeneratingStage(null);
    }
  }, [patientId]);

  // Which stage's regenerate dialog is open, and which is in flight.
  const [regenerateSlot, setRegenerateSlot] = useState(null);
  const [regeneratingStage, setRegeneratingStage] = useState(null);

  // ── Patient Report (Additional Documents panel) ─────────────────────────
  // Auto-finalises via rerenderCypAdhdPatientReportIfStale (review exit +
  // mount kick). The manual Generate button is removed; the retry handler
  // below serves the error "Try again" button only.
  const [isGeneratingPatientReport, setIsGeneratingPatientReport] = useState(false);
  const [isPatientReportDownloading, setIsPatientReportDownloading] = useState(false);

  const handleGeneratePatientReport = useCallback(async () => {
    setIsGeneratingPatientReport(true);
    try {
      // The trigger reports failures as { success: false, error } rather
      // than throwing — inspect the result or every failure is silent.
      const result = await TriggerRenderCypAdhdDocTemplate({
        patientId,
        stageKey: 'patientReport',
        clinicianId: auth.currentUser?.uid,
      });
      if (!result?.success) {
        toast.error(`Patient report generation failed: ${result?.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Patient report generation failed:', error);
      toast.error(`Patient report generation failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsGeneratingPatientReport(false);
    }
  }, [patientId]);

  const handleDownloadPatientReport = useCallback(async (format) => {
    setIsPatientReportDownloading(true);
    try {
      const result = await TriggerFetchLatestDocForDownload({
        reportStage: CYP_PATIENT_REPORT_STAGE,
        clinicianId: auth.currentUser?.uid,
        patientId,
        fileFormat: format,
      });
      if (!result.success) {
        toast.error(`Failed to download report: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error downloading patient report:', error);
      toast.error(`Error downloading report: ${error.message || 'Unknown error'}`);
    } finally {
      setIsPatientReportDownloading(false);
    }
  }, [patientId]);

  const documents = header?.documents || {};

  // ── Preview (per row) + bundle download (panel level) ───────────────────
  // One shared DocumentEmbedDialog renders the selected report PDF in an
  // iframe. Individual-row downloads are GONE (Sai, 28 Aug): sections are
  // downloaded through the panel-level Download Sections button, which opens
  // the adult ReportBundleDialog — the clinician picks which sections to
  // include and the generateClinicalReportBundle CF stitches one PDF.
  const [previewReportStage, setPreviewReportStage] = useState(null);
  const [previewLastGenerated, setPreviewLastGenerated] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showBundleDialog, setShowBundleDialog] = useState(false);

  const handleOpenPreview = useCallback((reportStage, lastGenerated) => {
    setPreviewReportStage(reportStage);
    setPreviewLastGenerated(lastGenerated || null);
    setIsPreviewOpen(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setIsPreviewOpen(false);
    setPreviewReportStage(null);
    setPreviewLastGenerated(null);
  }, []);

  // One list for the Rating Scales pills. Rebuilt only when the assigned
  // clinicians change, not on every row render.
  const ratingScalesClinicians = useMemo(
    () => ratingScalesAssignees(header?.clinicians),
    [header?.clinicians],
  );

  // Per-slot status. The purple ready-for-review state is gated on the
  // MODULE BEING FINISHED (Sai, 28 Aug): AI paragraphs exist from the first
  // section exit, but the row only turns purple once the clinician has
  // pressed "Finish <module>" — the adult dashboard's semantics, where the
  // {prefix}ReportGenerated stamp is written by module completion.
  const slotStatus = useCallback((slot) => {
    if (!slot.stageKey) return 'locked';
    const stageReport = reportContent?.[slot.stageKey];
    const hasOutputs = Boolean(
      stageReport?.outputs && Object.keys(stageReport.outputs).length > 0,
    );
    const isModuleFinished = assessmentStages?.[slot.stageKey]?.status === 'completed';
    if (slot.approvedFlag && stageReport?.[slot.approvedFlag]) return 'approved';
    // A generate-on-demand row has no module and no review: it is simply
    // done once a document has been produced.
    if (slot.generateOnDemand) return documents?.[slot.key]?.lastGenerated ? 'approved' : 'locked';
    if (hasOutputs && isModuleFinished) return 'ready-for-review';
    return 'locked';
  }, [reportContent, assessmentStages, documents]);

  // The bundle dialog's section list: the three renderable stages, in
  // pipeline order, with the same statuses the rows show. reportStage is what
  // the dialog displays and the CF logs; the id keys into the shared
  // section-to-documents-key mapping (frontend dialog + backend allowlist).
  // MEMOISED, like the adult page's visibleSectionsForDialog: the dialog's
  // selection effect depends on this array's identity, so a fresh array per
  // render loops it forever once the dialog is open.
  const bundleSections = useMemo(() => RENDERABLE_STAGES
    .filter(({ stageKey }) => STAGE_TO_BUNDLE_SECTION_ID[stageKey])
    .map(({ stageKey }) => {
    const slot = DOCUMENT_SLOTS.find((s) => s.stageKey === stageKey);
    return {
      id: STAGE_TO_BUNDLE_SECTION_ID[stageKey],
      // The dialog displays reportStage as the row label — use the same
      // titles the Report Builder rows show, not the internal stage strings.
      reportStage: slot.title,
      status: slotStatus(slot),
    };
  }), [slotStatus]);
  const bundleProcessingStatuses = useMemo(() => Object.fromEntries(
    RENDERABLE_STAGES
      .filter(({ stageKey }) => STAGE_TO_BUNDLE_SECTION_ID[stageKey])
      .map(({ stageKey }) => [
        STAGE_TO_BUNDLE_SECTION_ID[stageKey],
        renderStatuses[stageKey] === 'processing' ? 'processing' : 'idle',
      ]),
  ), [renderStatuses]);
  const hasApprovedSections = bundleSections.some((s) => s.status === 'approved');
  const isAnySectionProcessing = RENDERABLE_STAGES.some(
    ({ stageKey }) => STAGE_TO_BUNDLE_SECTION_ID[stageKey]
      && renderStatuses[stageKey] === 'processing',
  );
  const canDownloadSections = hasApprovedSections && !isAnySectionProcessing;

  const handleConfirmRegenerate = useCallback(async () => {
    if (!regenerateSlot || regeneratingStage) return;
    const { stageKey, regenerateSections, title } = regenerateSlot;
    // MSE and Risk Assessment exist on the CI stage doc only once the
    // clinician has recorded them; asking the Cloud Function to draft from a
    // missing field fails the call (and would abort this sequential loop
    // partway). Drop the empty ones from the run — a no-op for every other
    // stage and section.
    const sectionTitles = regenerateSections.filter((sectionTitle) => (
      hasCypCiPageSectionData(assessmentStages?.[stageKey], sectionTitle)
    ));
    setRegeneratingStage(stageKey);
    try {
      const result = await TriggerRegenerateCypAdhdStage({
        patientId,
        clinicianId: auth.currentUser?.uid,
        stageKey,
        sectionTitles,
      });
      if (!result?.success) throw result?.error || new Error('Regeneration failed');
      // Approvals were cleared server-side, so pull the fresh document in
      // rather than leaving the row showing the pre-regenerate state.
      await refreshReportContent?.(patientId, stageKey);
      setRegenerateSlot(null);
      toast.success(`${title} AI outputs regenerated. They need reviewing again.`);
    } catch (error) {
      console.error('CYP regeneration failed:', error);
      toast.error(
        'The AI outputs could not be regenerated just now. Please check your connection and try again.',
        { toastId: 'cyp-regenerate-error' },
      );
    } finally {
      setRegeneratingStage(null);
    }
  }, [regenerateSlot, regeneratingStage, patientId, refreshReportContent, assessmentStages]);

  return (
    <Layout activePage="dashboard">
      <Breadcrumb
        tupleList={[
          ['Patient Overview', `/patient/${patientId}/cyp-adhd`],
          ['Documents', null],
        ]}
      />

      {/* Patient header panel — same component shape as the autism and adult
          documents pages, including the route back to the overview.

          The lines under the name match the CYP Patient Overview header
          rather than the adult pages: a child has no account, so there is
          no email of their own to show (the adult layout left a blank line
          here). The parent/carer line and the Contract badge are the same
          components the overview renders, so a clinician moving between the
          two pages sees the same facts in the same place (tester feedback,
          Oct 2026). */}
      {header && (
        <div className="pl-8 pr-8">
          <div className="w-full px-6 py-5 bg-gray-50 rounded-xl border border-gray-200 justify-start items-center gap-14 inline-flex text-left mb-4">
            <div className="grow shrink basis-0 h-16 justify-start items-center gap-5 flex">
              <div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
                <div className="self-stretch text-gray-900 text-3xl font-semibold leading-9">
                  {header.firstName} {header.lastName}
                </div>
                {header.email ? (
                  <div className="self-stretch text-slate-800 text-base font-normal leading-normal">
                    {header.email}
                  </div>
                ) : null}
                <CypGuardianContactLines informants={header.informants} />
              </div>
            </div>
            <div className="flex justify-start items-center gap-3">
              <PatientContractTypeBadge
                label="Contract"
                organisation={claims?.organisation}
                assessmentType={header.customFields?.assessmentType}
                subContractType={header.customFields?.subContractType}
              />
              <Link
                to={`/patient/${patientId}/cyp-adhd`}
                className="px-4 py-2 font-bold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 inline-flex items-center gap-1"
              >
                Go to Patient Overview
                <ChevronRightIcon className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="flex-grow gap-4">
        <div className="max-w-7xl p-8 mx-auto text-left bg-white rounded-xl shadow-sm border transition-all duration-200">
          <div className="flex items-center justify-between ml-4 mb-2">
            <h1 className="text-2xl font-bold text-gray-900">{PANEL_COPY.panelTitle}</h1>

            {/* Panel-level Download Sections — the adult dashboard's button
                and gating: disabled until at least one section is approved
                and nothing is finalising. Opens the shared bundle dialog so
                the clinician picks which sections the stitched PDF includes
                (Sai, 28 Aug: downloads live here, not on the rows). */}
            <div className="flex items-center gap-6">
              <button
                type="button"
                data-tooltip-id="cyp-download-sections"
                onClick={() => setShowBundleDialog(true)}
                disabled={!canDownloadSections}
                className={`px-6 py-2.5 text-sm font-bold rounded-md
                    focus:outline-none focus:ring-2 focus:ring-violet-500
                    focus:ring-offset-2 flex items-center justify-center
                    ${canDownloadSections
                    ? 'bg-violet-900 text-white cursor-pointer hover:bg-violet-800'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                <DownloadIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                Download Sections
              </button>
              {!canDownloadSections && (
                <Tooltip
                  id="cyp-download-sections"
                  place="top"
                  className="z-50 text-xs font-semibold"
                  style={{
                    maxWidth: '250px',
                    whiteSpace: 'normal',
                    backgroundColor: 'rgba(34, 34, 34, 1)',
                    borderRadius: '15px',
                    color: '#fff',
                  }}
                >
                  {isAnySectionProcessing
                    ? 'Please wait for all documents to finish processing before downloading.'
                    : 'At least one section of the report needs to be generated and approved before you can download it.'}
                </Tooltip>
              )}
            </div>
          </div>

          <div className="relative">
            {/* Vertical line joining the numbered circles, behind them (z-10 on
                the circles keeps it from crossing the numbers). */}
            <div className="absolute left-[40px] top-12 bottom-12 w-0.5 bg-gray-200"></div>

            <div>
              {DOCUMENT_SLOTS.map((slot) => {
                const {
                  order, key, title, stageKey, reviewRoute, isPlaceholder, isRatingScales,
                } = slot;
                const meta = documents[key];
                const stageReport = stageKey ? reportContent?.[stageKey] : null;
                const hasOutputs = Boolean(
                  stageReport?.outputs && Object.keys(stageReport.outputs).length > 0,
                );
                const isModuleFinished = stageKey
                  ? assessmentStages?.[stageKey]?.status === 'completed'
                  : false;
                const status = slotStatus(slot);
                const isFinalising = Boolean(
                  stageKey && renderStatuses[stageKey] === 'processing',
                );
                // isPending: the section content rendered but the PDF is
                // being retried in the background. Row shows amber "Preparing
                // PDF" with Preview/Download disabled.
                const isPending = !isFinalising && getPdfAvailability(meta) === 'pending';

                // Regeneration replaces this stage's paragraphs and clears every
                // approval on them, so it is restricted to the clinician assigned
                // to the stage — the same person the review page lets edit.
                const assignedClinician = stageKey
                  ? header?.clinicians?.[STAGE_TO_CLINICIAN_KEY[stageKey]]
                  : null;
                const canRegenerate = Boolean(
                  hasOutputs
                  && slot.regenerateSections
                  && assignedClinician?.clinicianId
                  && assignedClinician.clinicianId === auth.currentUser?.uid,
                );
                const isRegenerating = regeneratingStage === stageKey;

                return (
                  <div key={key} className="relative">
                    <div
                      className={`
                        w-full flex items-center gap-6 my-1 py-2 px-4 rounded-lg transition-all duration-200
                        ${backgroundClass(status, isRegenerating || isFinalising || isPending)}
                      `}
                    >
                      <div
                        className={`
                          relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2
                          flex items-center justify-center font-semibold text-lg
                          ${circleClass(status, isRegenerating || isFinalising || isPending)}
                        `}
                      >
                        {order}
                      </div>

                      <div className="flex-1 flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                            <StatusIcon
                              status={status}
                              isRegenerating={isRegenerating}
                              isFinalising={isFinalising}
                              isPending={isPending}
                            />
                          </div>

                          <div className="flex items-center gap-2 mt-1">
                            {/* The approval line carries the date and the pill
                                names the APPROVER, exactly as the adult
                                dashboard's row reads ("AI outputs last approved
                                on <date> by <badge>").
                                Rating Scales has no approver. Before a document
                                exists it names the assigned clinicians, the
                                same pills the adult rating-scales row shows.
                                Once generated, adult replaces that line with
                                "Last updated on <date>" and drops the pills. */}
                            {isRatingScales && status === 'approved' && meta?.lastGenerated ? (
                              <p className="text-sm italic text-gray-700">
                                Last updated on {formatDateTime(meta.lastGenerated)}
                              </p>
                            ) : (
                              <>
                                <p className="text-sm italic text-gray-700">
                                  {isPlaceholder
                                    ? slot.placeholderNote
                                    : isRatingScales
                                      ? 'Assigned to'
                                      : status === 'approved' && stageReport?.lastReviewedAt
                                        ? `AI outputs last approved on ${formatDateTime(stageReport.lastReviewedAt)} by`
                                        : assignmentLabel(status, isPlaceholder)}
                                </p>
                                {/* No "Generated <date>" chip: adult carries one
                                    date per row, in the italic approval line. */}
                                {!isPlaceholder && (isRatingScales ? (
                                  ratingScalesClinicians.length > 0 ? (
                                    ratingScalesClinicians.map((clinician) => (
                                      <div
                                        key={clinician.id}
                                        title={clinician.tooltip}
                                        className="inline-flex border items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 transition-colors duration-200 cursor-help"
                                      >
                                        <span>{clinician.name}</span>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="inline-flex border items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 transition-colors duration-200 cursor-help">
                                      <span>Unassigned</span>
                                    </div>
                                  )
                                ) : (
                                  <div className="inline-flex border items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 transition-colors duration-200 cursor-help">
                                    <span>
                                      {(status === 'approved' && stageReport?.reviewedByName)
                                        || assignedClinician?.clinicianName
                                        || 'Unassigned'}
                                    </span>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                          {/* Answers-changed warning: shown on the CI or SSI
                              row when an informant's form answers were updated
                              after that report was generated. Alerts the
                              clinician that drafted paragraphs may be stale. */}
                          {stageKey === 'clinicalInterview' && ciAnswersChanged && (
                            <p className="flex items-center gap-1 text-xs font-medium text-amber-700 mt-1">
                              <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                              Respondent answers changed after this report was generated
                            </p>
                          )}
                          {stageKey === 'semiStructuredInterview' && ssiAnswersChanged && (
                            <p className="flex items-center gap-1 text-xs font-medium text-amber-700 mt-1">
                              <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                              Respondent answers changed after this report was generated
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-3">
                          {/* Why a stage with no action has none, said plainly
                              rather than shown as a dead button. A stage whose
                              paragraphs exist but whose module is unfinished
                              says so — the purple state waits for "Finish". */}
                          {/* Rows with no AI review step carry a Generate
                              button rather than waiting on an approval that
                              never comes: the appendix as soon as scales
                              exist. */}
                          {slot.generateOnDemand
                            && !isFinalising && (
                            <button
                              type="button"
                              onClick={() => handleGenerateOnDemand(stageKey)}
                              disabled={generatingStage === stageKey}
                              className={`flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200 ${
                                generatingStage === stageKey
                                  ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                  : meta?.lastGenerated
                                    ? 'text-gray-900 border border-gray-300 hover:bg-gray-100'
                                    : 'bg-violet-800 text-white hover:bg-violet-700'
                              }`}
                            >
                              {/* Keyed to whether a DOCUMENT exists, not to
                                  the row status: a finished PAD reads
                                  'approved' before it has ever been
                                  rendered, and offering "Regenerate" for a
                                  document that does not exist is a lie. */}
                              {generatingStage === stageKey
                                ? 'Generating…'
                                : meta?.lastGenerated ? 'Regenerate' : 'Generate'}
                            </button>
                          )}

                          {!isPlaceholder && !isRatingScales && !slot.generateOnDemand
                            && status === 'locked' && (
                            <div className="text-xs text-gray-500 italic">
                              {hasOutputs && !isModuleFinished
                                ? `Available once the ${title} module is finished`
                                : isModuleFinished
                                  // Finished module, no outputs yet: generation
                                  // is still landing (or a call failed —
                                  // re-entering and exiting a section
                                  // re-triggers it). Never claim the stage is
                                  // incomplete when the clinician finished it.
                                  ? 'AI outputs are still being prepared. Check back shortly'
                                  // Adult names the module here rather than
                                  // saying "the assessment stage", so a
                                  // clinician reading five rows can tell WHICH
                                  // module each one is waiting on.
                                  : `Available after ${title} assessment module is completed`}
                            </div>
                          )}

                          {SHOW_REGENERATE_AI_OUTPUTS_BUTTON && canRegenerate && (
                            <button
                              type="button"
                              onClick={() => setRegenerateSlot(slot)}
                              disabled={isRegenerating}
                              className={`flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200 ${
                                isRegenerating
                                  ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                                  : 'text-gray-900 border border-gray-300 hover:bg-gray-100'
                              }`}
                            >
                              {isRegenerating ? 'Regenerating…' : 'Regenerate AI outputs'}
                            </button>
                          )}

                          {reviewRoute && hasOutputs && isModuleFinished && (
                            <button
                              type="button"
                              onClick={() => navigate(reviewRoute(patientId))}
                              className={`flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200 ${
                                status === 'approved'
                                  ? 'text-gray-900 border border-gray-300 hover:bg-green-200'
                                  : 'bg-violet-800 text-white hover:bg-violet-700'
                              }`}
                            >
                              {status === 'approved' ? 'Review Paragraphs' : 'Review AI Outputs'}
                            </button>
                          )}

                          {/* The document column, state-aware. While the
                              renderer runs the ROW carries the amber
                              Finalising state (adult parity) — no extra text
                              here. A failed render offers retry; a rendered
                              document offers Preview (downloads live at the
                              panel level); "Awaiting template" is only ever
                              true of the placeholder rows. */}
                          {stageKey && renderStatuses[stageKey] === 'error' && (
                            <button
                              type="button"
                              onClick={() => handleRetryRender(stageKey)}
                              disabled={retryingStage === stageKey}
                              className="bg-white text-primary border border-primary hover:bg-primary/5 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200 disabled:opacity-50"
                            >
                              {retryingStage === stageKey ? 'Retrying…' : 'Document failed. Try again'}
                            </button>
                          )}
                          {meta?.lastGenerated && stageKey
                            && renderStatuses[stageKey] !== 'processing'
                            && renderStatuses[stageKey] !== 'error' && (
                            /* Adult Preview button verbatim: grey outline with
                               the magnifying-glass icon (Sai, 28 Aug). */
                            <button
                              type="button"
                              onClick={() => handleOpenPreview(
                                slotReportStage(slot),
                                meta.lastGenerated,
                              )}
                              className="text-gray-900 border border-gray-300 hover:bg-gray-100 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                            >
                              <SearchIcon className="w-4 h-4 mr-1.5" aria-hidden="true" />
                              Preview
                            </button>
                          )}
                          {/* Deliberately no second grey hint here. The adult
                              dashboard gives a row ONE explanation; this page
                              used to add "Document generates when the review is
                              approved" underneath the locked text, so a locked
                              row carried two different-sounding reasons at once
                              (Eve, 1 Sept: "makes no sense"). */}
                          {/* Rating Scales generates on demand from its own
                              button above — the scores exist as soon as an
                              informant submits, so there is nothing to wait
                              for and no reason line to show. */}
                          {/* Placeholder rows say their one thing in the
                              italic line above; nothing goes in the action
                              column, so the row stays visibly inert. */}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Additional Documents — the Patient Report in its own panel, as on
            the adult dashboard. Rendered by the cyp_car 'patientReport'
            pipeline from every stage's APPROVED outputs; the PDF/DOCX format
            choice lives HERE only (individual sections above are
            bundle-downloaded as PDF). Auto-finalises once all four section
            reports (CI + SSI + DD + PAD) are approved — the manual Generate
            button is gone (Adult Autism parity). The "Try again" button
            appears only on render error. */}
        {(() => {
          const patientReportMeta = documents.cypAdhdPatientReport;
          // Uniform four-section gate: all four assessment stage reports must
          // be approved before the Patient Report can render. Matches the gate
          // in autoCypAdhdPatientReport.js.
          const allSectionsApproved = [
            'clinicalInterview', 'semiStructuredInterview',
            'diagnosticDecision', 'postAssessmentDiscussion',
          ].every((stageKey) => {
            const slot = DOCUMENT_SLOTS.find((docSlot) => docSlot.stageKey === stageKey);
            return Boolean(slot && reportContent?.[stageKey]?.[slot.approvedFlag]);
          });
          const isPatientReportFinalising = renderStatuses.patientReport === 'processing'
            || isGeneratingPatientReport;
          const isPatientReportError = !isPatientReportFinalising
            && renderStatuses.patientReport === 'error';
          // isPending: the patient report rendered but the PDF is being
          // retried in the background.
          const isPatientReportPending = !isPatientReportFinalising
            && getPdfAvailability(patientReportMeta) === 'pending';
          const patientReportStatus = patientReportMeta ? 'approved' : 'locked';
          // Preview/Download hide only while a render is in flight (the
          // stored files are being superseded). A generated report stays
          // previewable and downloadable when a section approval is revoked
          // afterwards — the clinician is told it may be out of date instead
          // of having the document taken away (HK testing, 2 Sept).
          const patientReportActionsVisible = Boolean(
            patientReportMeta && !isPatientReportFinalising && !isPatientReportPending,
          );
          const isPatientReportStale = Boolean(patientReportMeta && !allSectionsApproved);
          return (
            <div className="my-4 max-w-7xl p-8 mx-auto text-left bg-white rounded-xl shadow-sm border transition-all duration-200">
              <div className="ml-4 mb-6">
                <h1 className="text-2xl font-bold text-gray-900">
              {getAdditionalDocumentsPanelTitle(true)}
            </h1>
              </div>
              <div className="relative">
                <div className="absolute left-[40px] top-0 bottom-0 w-0.5 bg-gray-200"></div>
                <div>
                  <div className="relative">
                    <div className={`w-full flex items-center gap-6 my-1 py-2 px-4 rounded-lg transition-all duration-200 ${backgroundClass(patientReportStatus, isPatientReportFinalising || isPatientReportPending)}`}>
                      <div className={`relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2 flex items-center justify-center font-semibold text-lg ${circleClass(patientReportStatus, isPatientReportFinalising || isPatientReportPending)}`}>
                        1
                      </div>
                      <div className="flex-1 flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="text-lg font-semibold text-gray-900">Patient Report</h3>
                            <StatusIcon
                              status={patientReportStatus}
                              isFinalising={isPatientReportFinalising}
                              isPending={isPatientReportPending}
                            />
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {/* Adult's Patient Report row (AdditionalDocuments.js):
                                once a document exists the line is "Last updated
                                on <date>" with NO pill; before that it names the
                                assignee, as every other row does. */}
                            {patientReportMeta?.lastGenerated ? (
                              <p className="text-sm italic text-gray-700">
                                Last updated on {formatDateTime(patientReportMeta.lastGenerated)}
                              </p>
                            ) : (
                              <>
                                <p className="text-sm italic text-gray-700">Assigned to</p>
                                <div className="inline-flex border items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 transition-colors duration-200 cursor-help">
                                  <span>
                                    {header?.clinicians?.clinicianLead?.clinicianName || 'Unassigned'}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {!allSectionsApproved && !patientReportMeta && (
                            <span className="text-xs text-gray-500 italic">
                              Automatically generated once all sections of the Clinical Assessment Report are approved
                            </span>
                          )}
                          {isPatientReportStale && (
                            <span className="flex items-center gap-1 text-xs font-medium text-amber-700">
                              <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                              A section changed since this report was generated. Approve all
                              sections to regenerate automatically.
                            </span>
                          )}
                          {isPatientReportError && (
                            <button
                              type="button"
                              onClick={handleGeneratePatientReport}
                              className="bg-white text-primary border border-primary hover:bg-primary/5 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                            >
                              Report failed. Try again
                            </button>
                          )}
                          {patientReportActionsVisible && (
                            <DownloadDropdown
                              onDownload={handleDownloadPatientReport}
                              isDownloading={isPatientReportDownloading}
                              label="Download Report"
                            />
                          )}
                          {patientReportActionsVisible && isPdfReady(patientReportMeta) && (
                            <button
                              type="button"
                              onClick={() => handleOpenPreview(CYP_PATIENT_REPORT_STAGE, patientReportMeta.lastGenerated)}
                              className="text-gray-900 border border-gray-300 hover:bg-gray-100 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                            >
                              <SearchIcon className="w-4 h-4 mr-1.5" aria-hidden="true" />
                              Preview
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Panel-level bundle download: the adult dialog, fed the CYP sections.
          The clinician picks which sections the stitched PDF includes; the
          generateClinicalReportBundle CF resolves each section id to its
          documents.{key} through the shared allowlist. */}
      <ReportBundleDialog
        open={showBundleDialog}
        onOpenChange={setShowBundleDialog}
        patientId={patientId}
        patientDataModel={header}
        visibleSections={bundleSections}
        processingStatuses={bundleProcessingStatuses}
        title="Download Sections"
      />

      {/* Regenerate confirmation. Wording mirrors the autism dashboard's
          equivalent dialog: the clinician must understand that every
          paragraph is replaced and every approval on this stage is cleared. */}
      <AlertDialog.Root
        open={Boolean(regenerateSlot)}
        onOpenChange={(open) => { if (!open && !regeneratingStage) setRegenerateSlot(null); }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000]" />
          <AlertDialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[600px] max-w-[90vw] max-h-[85vh] overflow-y-auto z-[1001] text-left">
            <AlertDialog.Title className="text-xl font-bold">
              Regenerate AI outputs{regenerateSlot ? ` (${regenerateSlot.title})` : ''}
            </AlertDialog.Title>
            <AlertDialog.Description asChild>
              <div className="mt-3 text-sm text-gray-700 space-y-3">
                <p>
                  This replaces every AI paragraph for this report section. Please
                  read what that means before continuing:
                </p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>
                    New paragraphs are generated from the{' '}
                    <span className="font-semibold">latest available inputs</span>, including
                    any changes the parent or carer and the school representative have made
                    to their questionnaires since the last generation, plus your own notes
                    and decisions.
                  </li>
                  <li>
                    Any <span className="font-semibold">edits you made</span> to these
                    paragraphs will be replaced.
                  </li>
                  <li>
                    Every paragraph returns to{' '}
                    <span className="font-semibold">needs review</span>, so you will need to
                    review and approve them again.
                  </li>
                </ul>
              </div>
            </AlertDialog.Description>
            <div className="mt-6 flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  disabled={Boolean(regeneratingStage)}
                  className="py-2 px-4 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <button
                type="button"
                onClick={handleConfirmRegenerate}
                disabled={Boolean(regeneratingStage)}
                className="py-2 px-4 text-sm font-semibold rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50"
              >
                {regeneratingStage ? 'Regenerating…' : 'Regenerate outputs'}
              </button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Shared embed dialog — the selected report PDF in an iframe, the
          autism dashboard's flow (fetchLatestDocumentForEmbed resolves the
          file from documents.{key} by report-stage string). */}
      <DocumentEmbedDialog
        isOpen={isPreviewOpen}
        onClose={handleClosePreview}
        reportStage={previewReportStage}
        clinicianId={auth.currentUser?.uid}
        patientId={patientId}
        lastGenerated={previewLastGenerated}
      />
    </Layout>
  );
}
